import type {
	BottleneckBucket,
	BottleneckQuartilesBasis,
} from '../../../../redux/api/bottleneck-report';

/**
 * What each stage is for, and the statuses that put a job there.
 *
 * `rules` mirrors `classify()` in
 * `server/fastlane/orders/services/bottleneck_board.py`. That function decides
 * every number on this board, so if the two ever disagree the text here is the
 * one that is wrong. Anyone editing the classifier should edit these too.
 */
export interface BucketExplainer {
	intent: string;
	rules: string[];
}

/**
 * Why the rules can be read as short lists rather than long exclusions: the
 * classifier walks the stages in a fixed order and a job lands in the first
 * one that matches, which is what keeps them mutually exclusive.
 */
export const PRECEDENCE_NOTE =
	'Stages are checked in a fixed order and a job lands in the first one it ' +
	'matches, so it can never appear twice.';

export const BUCKET_EXPLAINERS: Record<BottleneckBucket, BucketExplainer> = {
	new: {
		intent:
			'Nobody has finished choosing material yet, so the job has not really ' +
			'started. This is the front of the queue.',
		rules: ['Material Selection is Not Started or In Progress.'],
	},
	material_selected: {
		intent:
			'Material is chosen and the job is waiting on engineering review — ' +
			'ballooning and the programming template — before it can be programmed.',
		rules: [
			'Material Selection is Completed.',
			'Ballooning or Programming Template is still Not Started or In Progress.',
		],
	},
	program_review: {
		intent:
			'Review is finished and the job is sitting with CAM. Work piling up ' +
			'here means programming is the constraint.',
		rules: [
			'Ballooning is Completed.',
			'Programming Template is Completed.',
			'CAM Programming is Not Started or In Progress.',
		],
	},
	waiting_on_material: {
		intent:
			'Programming is done and the shop is waiting on a supplier. Nothing ' +
			'here can be fixed on the floor.',
		rules: [
			'CAM Programming is Completed.',
			'Material has been requested, or ordered on a purchase order that is not yet fully received.',
			'No material reservation exists yet.',
		],
	},
	waiting_on_kitting: {
		intent:
			'Material has been cut but the kit has not been built, so the job ' +
			'cannot go to a machine. Kitting is the hold-up.',
		rules: [
			'CAM Programming is Completed.',
			'Kitting is Not Started or In Progress.',
			'Material Prep is In Progress or Completed.',
		],
	},
	ready_to_kit_and_cut: {
		intent:
			'Material is on hand and programming is done, so the job is ready for ' +
			'the floor to pick up. Nothing is blocking it.',
		rules: [
			'CAM Programming is Completed.',
			'A material reservation exists.',
			'Material Prep is Not Started.',
			'Kitting is Not Started or In Progress.',
		],
	},
	waiting_on_cutting: {
		intent:
			'The kit is built and material is reserved, but nobody has cut it ' +
			'yet. Cutting is the only thing left before the machine.',
		rules: [
			'CAM Programming is Completed.',
			'Kitting is Completed.',
			'Material Prep is Not Started.',
			'A material reservation exists.',
		],
	},
	ready_to_machine: {
		intent:
			'Kit and material are both ready and the job is queued for a machine. ' +
			'A pile here means machine capacity, not preparation, is the limit — ' +
			'unless the Ticketed count is high, in which case some of these jobs ' +
			'are not queued at all, they are stuck.',
		rules: [
			'Kitting is Completed.',
			'Material Prep is In Progress or Completed.',
			'Neither Machine Setup nor Machining has started.',
		],
	},
	machining: {
		intent:
			'The job is at the machine. It counts from the moment setup starts, ' +
			'not from when someone marks the spindle running.',
		rules: [
			'Machine Setup is In Progress or Completed, or Machining is In Progress.',
			'Quality has not signed off yet.',
		],
	},
	done: {
		intent:
			'Parts are made and inspected, waiting to ship. Work sitting here is ' +
			'finished revenue that has not left the building.',
		rules: ['Quality is Completed.', 'Delivery is not yet Delivered in Full.'],
	},
	uncategorized: {
		intent:
			'A punch list of jobs whose statuses do not describe any stage. This ' +
			'is a data problem to chase, not a slow stage.',
		rules: [
			'No stage above matched.',
			'Most often programming is finished with no material reserved and no ' +
				'purchase order still open.',
		],
	},
	delivered: {
		intent:
			'Recently shipped work, kept as a reference for how long jobs took. ' +
			'It is history, not something the shop is waiting on.',
		rules: [
			'Delivery is Delivered in Full, which outranks every other status.',
		],
	},
};

/**
 * Quartiles are per stage, over the jobs on this board — not all-time factory
 * history. Delivered measures something different, hence the two variants.
 */
export const QUARTILE_EXPLAINERS: Record<BottleneckQuartilesBasis, string> = {
	stay:
		'25%, 50% and 75% are how long finished stays in this stage took: a ' +
		'quarter of them were faster than the 25% figure, half were faster than ' +
		'the 50%, and Max is the slowest. A job still sitting in the stage has ' +
		'no finished stay yet, so it is not counted — which is why a busy stage ' +
		'nobody ever leaves can show no numbers at all. Samples is how many ' +
		'stays those figures are based on.',
	lead_time:
		'25%, 50% and 75% are total lead time — first record to handoff — for ' +
		'the shipped jobs listed below: half of them took less than the 50% ' +
		'figure, and Max is the longest. Delivered work never leaves this ' +
		'stage, so time spent in it would say nothing.',
};

/**
 * Why a ticketed job is not its own stage: a ticket says the job is stuck, not
 * where it is stuck. Moving it out of its stage would remove it from the count
 * of work waiting on that stage, which is the number the board exists to show.
 */
export const TICKETED_EXPLAINER =
	'How many of these line items an operator has raised a shop-floor ticket ' +
	'against that nobody has closed yet — jobs, not tickets, so a job with two ' +
	'open tickets still counts once. They stay in this stage and in its count, ' +
	'because they are still work this stage is waiting on; the flag is there so ' +
	'the count is not read as "ready to go". Open the stage to see which ones, ' +
	'and the job page for what was said. Tickets are raised and resolved in the ' +
	'Shop Floor App, not here.';

export const BAR_EXPLAINER =
	'Each bar is split at now: the coloured run to the left is where the job ' +
	'has spent its life, one colour per stage, and the pale run to the right ' +
	'is what is left before the customer ship date. Red to the right of the ' +
	'line means past due. Chevrons mark the few jobs too long to fit the ' +
	'shared scale. Hover a bar for the breakdown.';
